import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { merge, takeUntil } from 'rxjs';
import { ParentCustomerDto } from '@ups/xplat/api/dto';
import {
  SearchCustomerService,
  SearchCustomerVPService,
} from '@ups/xplat/api/services';
import { dynamicTypeaheadCustomersFilter } from '@ups/xplat/dynamic-render';
import { DynamicTypeaheadWebBaseComponent } from '../base/dynamic-typeahead.web-base-component';
import { withAdditionalCustomers } from './additional-customer-search';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadCustomersComponent),
  multi: true,
};

@Component({
  selector: 'ups-dynamic-typeahead-customers',
  templateUrl: 'dynamic-typeahead-customers.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  // WI 35584: template state is marked at each async write (loading, search results, target
  // updates) in the shared dynamic-render bases.
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['dynamic-typeahead-customers.component.scss'],
  standalone: false,
})
export class DynamicTypeaheadCustomersComponent
  extends DynamicTypeaheadWebBaseComponent
  implements OnInit
{
  private readonly searchCustomerService = inject(SearchCustomerService);
  private readonly searchCustomerVPService = inject(SearchCustomerVPService);

  /**
   * Bug 35680: true while the control holds a picked customer and is enabled. A signal, written from
   * the control's own value/status streams, so the OnPush view repaints on every write (a pick, a
   * reset, a disable) without a method call in the template.
   */
  readonly canClear = signal(false);

  constructor() {
    super();
    this.queryFilter = dynamicTypeaheadCustomersFilter;
  }

  ngOnInit(): void {
    super.ngOnInit();
    const customerSearch: typeof this.searchQuery = this.config?.options
      ?.pointToSparta
      ? this.searchCustomerService.search.bind(this.searchCustomerService)
      : this.searchCustomerVPService.search.bind(this.searchCustomerVPService);
    this.searchQuery = withAdditionalCustomers(
      customerSearch,
      this.config?.options?.additionalCustomerSearch
    );
    this.trackCanClear();
  }

  /**
   * The X button: null the form control, then run the normal selection path with null so the host's
   * (valueChange) handler and the config's updateOnValueChange logic see the clear like any other pick.
   */
  clearSelection(): void {
    if (!this.canClear()) {
      return;
    }
    this.updateValue(null);
    this.setValue(null);
    this.valueChangeItem(null as never); // null is the cleared selection the shared base accepts
    this.canClear.set(false);
  }

  private trackCanClear(): void {
    const name = this.config?.formControlName;
    const control = name ? this.group?.controls?.[name] : undefined;
    if (!control || !this.config?.options?.clearable) {
      return;
    }
    const refresh = () => this.canClear.set(this.hasPickedCustomer(control));
    refresh();
    merge(control.valueChanges, control.statusChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(refresh);
  }

  /** A real pick carries an id; Kendo's placeholder row ({ CustomerName: placeholder }) does not. */
  private hasPickedCustomer(control: {
    value: unknown;
    disabled: boolean;
  }): boolean {
    const value = control.value as Partial<ParentCustomerDto> | null;
    return (
      !control.disabled &&
      (value?.ParentCustomerID != null ||
        value?.VPParentCustomerKeyID != null ||
        value?.ProspectiveCustomerId != null)
    );
  }

  toggleFavoriteClient(client: ParentCustomerDto, e: PointerEvent) {
    if (client.ProspectiveCustomerId) {
      e.stopPropagation(); // US 36095: a dbo prospect has no VP favorite row
      return;
    }
    client.IsUserFavorite = !client.IsUserFavorite;
    if (this.config?.options?.pointToSparta) {
      this.searchCustomerService.addOrRemoveFavoriteCustomer(
        client.ParentCustomerID
      );
    } else {
      this.searchCustomerVPService.addOrRemoveFavoriteCustomer(
        client.VPParentCustomerKeyID
      );
    }
    e.stopPropagation();
  }
}
